import {createContext, useCallback, useContext, useMemo} from "react";
import { useNavigate } from "react-router-dom";
import { usePostHog } from 'posthog-js/react';
import { useLocalStorage } from "./useLocalStorage";
import { Utils } from "../utils/utils";
import { useLinkCookieConsentSessionMutation } from "../services/nestjs-base-api";
import { setExploringOnboarding } from "../components/MarketerView/shell/onboardingExplore";

const COOKIE_CONSENT_SESSION_KEY = 'cookie_consent_session_id';

// Where a successful login should land.
//
// ?redirect= exists so a logged-out user who clicks a deep link resumes it
// after signing in. It must not be able to drop someone into a section their
// role has no business in: platform admins belong under /admin, agency users
// without a client context under /agency, everyone else in the app proper.
//
// Previously any redirect short-circuited the role branches entirely and we
// relied on each destination layout re-checking the role and bouncing the user
// back. That holds only for as long as every route remembers to do so, so the
// check belongs here, at the single point where login decides where to go.
//
// Also rejects anything that isn't a same-origin absolute path, so a crafted
// ?redirect=//evil.example can't turn sign-in into an open redirect.
export const resolveLoginDestination = (
    user: any,
    redirectPath?: string,
): string => {
    const isPlatformAdmin = Utils.userIsPlatformAdmin(user);
    // An agency user viewing a client (agencyContext set) browses the normal
    // app, so only a context-less agency user is pinned to /agency.
    const isAgencyHome = Utils.userIsAgencyUser(user) && !user?.agencyContext;

    const home = isPlatformAdmin
        ? '/admin/dashboard'
        : isAgencyHome
            ? '/agency'
            : '/dashboard';

    if (!redirectPath) return home;

    // Same-origin absolute paths only: "/x" passes; "//host", "/\host" and
    // "https://host" do not.
    if (!/^\/(?![/\\])/.test(redirectPath)) return home;

    const pathname = redirectPath.split(/[?#]/)[0];
    const inSection = (section: string) =>
        pathname === section || pathname.startsWith(`${section}/`);
    const isAdminPath = inSection('/admin');
    const isAgencyPath = inSection('/agency');

    if (isPlatformAdmin) return isAdminPath ? redirectPath : home;
    if (isAgencyHome) return isAgencyPath ? redirectPath : home;

    return isAdminPath || isAgencyPath ? home : redirectPath;
};

interface IAuthContext {
    user: any,
    login: (data: any, redirectPath?: string) => void,
    logout: () => void,
}

const AuthContext = createContext<IAuthContext>({
    user: null,
    login: () => {},
    logout: () => {}
});

export const AuthProvider = ({ children }: any) => {
    const [user, setUser] = useLocalStorage("user", null);
    const navigate = useNavigate();
    const posthog = usePostHog();
    const [linkCookieConsentSession] = useLinkCookieConsentSessionMutation();

    const login = useCallback(async (data: any, redirectPath?: string) => {
        setUser(data);

        // Reset any "skip onboarding, explore for now" flag on every login.
        // Skipping is a within-session "not now", not a permanent opt-out — we
        // re-present the setup wizard each time an un-onboarded user signs in,
        // so onboarding is pushed until it's actually done. Clearing here (not
        // just on logout) also covers token-expiry re-logins.
        if (data?.id !== undefined) {
            setExploringOnboarding(data.id, false);
        }

        // Link cookie consent session to user (fire and forget)
        const sessionId = localStorage.getItem(COOKIE_CONSENT_SESSION_KEY);
        if (sessionId && data?.jwtToken) {
            linkCookieConsentSession({ sessionId, jwtToken: data.jwtToken }).catch(() => {
                // Ignore errors - this is not critical
            });
        }

        // Identify user in PostHog. Never re-identify onto the impersonated
        // customer: that would rebind this browser's distinct_id to their
        // person, so our support session is recorded as their activity. The
        // before_send hook drops the events themselves; skipping identify keeps
        // the person binding clean too.
        if (posthog && data && !data.impersonation) {
            posthog.identify(data.id?.toString() || data.email, {
                email: data.email,
                firstName: data.firstName,
                lastName: data.lastName,
                organisationId: data.organisation?.id,
                organisationName: data.organisation?.name,
                roles: typeof data.roles === 'string' ? JSON.parse(data.roles) : data.roles,
                plan: data.organisation?.plan,
                phoneNumber: data.phoneNumber,
                user_type: Utils.userIsPlatformAdmin(data) ? 'admin' : 'user'
            });
        }

        // Follow ?redirect= when it suits this user's role, otherwise send them
        // to their own home. See resolveLoginDestination.
        navigate(resolveLoginDestination(data, redirectPath), { replace: true });
    }, [navigate, setUser, posthog, linkCookieConsentSession]);

    const logout = useCallback(() => {
        // Reset PostHog identification
        if (posthog) {
            posthog.reset();
        }

        // Clear both user and token from localStorage
        setUser(null);
        localStorage.removeItem('token');

        // Clear sessionStorage to prevent data leakage between users
        // This is especially important for onboarding wizard state
        sessionStorage.clear();

        navigate("/", { replace: true });
    }, [navigate, setUser, posthog]);

    const value = useMemo(() => ({
        user,
        login,
        logout
    }), [login, logout, user]);

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
    return useContext(AuthContext);
};
